<template>
  <div class="grid" data-cy="advanced-search-container">
    <div class="col">
      <span class="p-input-icon-left">
        <icon class="text-gray-600" :icon="['far', 'search']" />
        <InputText
          v-model="searchFields.globalSearchText"
          type="search"
          placeholder="Search by sizing name..."
          class="w-full"
          data-cy="global-search-input"
        />
      </span>
    </div>
    <LabelledToggle
      v-if="!showAdvanced"
      v-model="isAdvanced"
      class="col-fixed text-right"
      text="Advanced Filters"
      data-cy="advanced-filters-toggle"
    />
    <transition name="horizontal-slide">
      <div v-if="isAdvanced" class="col-12" data-cy="advanced-filters">
        <div class="grid">
          <div v-if="!hideWell" class="col-12 md:col-3">
            <span class="p-input-icon-left">
              <icon class="text-gray-600" :icon="['fas', 'oil-well']" />
              <InputText
                id="selected-well"
                v-model="searchFields.wellSearchText"
                type="search"
                placeholder="Well"
                class="w-full"
                data-cy="well-search-input"
              />
            </span>
          </div>
          <div class="col-12 md:col-3">
            <span class="p-input-icon-left">
              <icon class="text-gray-600" :icon="['fas', 'user']" />
              <InputText
                id="selected-customer"
                v-model="searchFields.customerSearchText"
                type="search"
                placeholder="Customer"
                class="w-full"
                data-cy="customer-search-input"
              />
            </span>
          </div>
          <div v-if="!hideOwner" class="col-12 md:col-3">
            <span class="p-input-icon-left">
              <icon class="text-gray-600" :icon="['far', 'house-user']" />
              <InputText
                id="selected-owner"
                v-model="searchFields.ownerSearchText"
                type="search"
                placeholder="Owner"
                class="w-full"
                data-cy="owner-search-input"
              />
            </span>
          </div>
          <div class="col">
            <span class="p-input-icon-left">
              <icon class="text-gray-600" :icon="['far', 'calendar']" />
              <Calendar
                id="date-range"
                v-model="searchFields.dateRange"
                v-tooltip.top="'Sizings modified between start and end dates'"
                class="w-full"
                selection-mode="range"
                :manual-input="false"
                show-button-bar
                placeholder="updated between mm/dd/yyyy and mm/dd/yyyy"
                data-cy="date-range-input"
              />
            </span>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import type { SizingsSearch } from "@/components/types";
import type {
  PerformanceEvaluationsQueryVariables,
  ScenariosQueryVariables,
  SizingsWithScenarioIdsQueryVariables
} from "gql/api";

import { reactive, ref } from "vue";

import Calendar from "primevue/calendar";
import LabelledToggle from "@/components/LabelledToggle.vue";

import {
  sizingsByTextSearch,
  sizingsByOwnerName,
  sizingsByWellName,
  sizingsByCustomerName,
  sizingsByDateRange
} from "@/models/sizings/predicates";
import { watchDebounced } from "@vueuse/core";
import {
  perfevalsByTextSearch,
  perfevalsByWellName,
  perfevalsByCustomerName,
  perfevalsByDateRange
} from "@/models/performance_evaluations/predicates";
import {
  scenariosByTextSearch,
  scenariosByOwnerName,
  scenariosByWellName,
  scenariosByCustomerName,
  scenariosByDateRange
} from "@/models/scenarios/predicates";

const props = defineProps<{
  sizingsWhere: SizingsWithScenarioIdsQueryVariables["where"];
  scenariosWhere?: ScenariosQueryVariables["where"];
  perfevalsWhere?: PerformanceEvaluationsQueryVariables["where"];
  hideOwner?: boolean;
  hideWell?: boolean;
  isAdvanced?: boolean;
}>();

const emit = defineEmits<{
  (
    e: "update:sizings-where",
    value: SizingsWithScenarioIdsQueryVariables["where"]
  ): void;
  (e: "update:scenarios-where", value: ScenariosQueryVariables["where"]): void;
  (
    e: "update:perfevals-where",
    value: PerformanceEvaluationsQueryVariables["where"]
  ): void;
  (e: "is-empty", value: boolean): void;
}>();

const searchFields = reactive<SizingsSearch>({
  dateRange: [],
  globalSearchText: "",
  ownerSearchText: "",
  customerSearchText: "",
  wellSearchText: ""
});

const isEmpty = ref(true);

watchDebounced(
  searchFields,
  newVal => {
    emit("update:sizings-where", {
      ...props.sizingsWhere,
      _and: [
        sizingsByTextSearch(newVal.globalSearchText),
        sizingsByOwnerName(newVal.ownerSearchText),
        sizingsByWellName(newVal.wellSearchText),
        sizingsByCustomerName(newVal.customerSearchText),
        sizingsByDateRange(newVal.dateRange)
      ]
    });
    emit("update:scenarios-where", {
      ...props.scenariosWhere,
      _and: [
        scenariosByTextSearch(newVal.globalSearchText),
        scenariosByOwnerName(newVal.ownerSearchText),
        scenariosByWellName(newVal.wellSearchText),
        scenariosByCustomerName(newVal.customerSearchText),
        scenariosByDateRange(newVal.dateRange)
      ]
    });
    emit("update:perfevals-where", {
      ...props.perfevalsWhere,
      _and: [
        perfevalsByTextSearch(newVal.globalSearchText),
        perfevalsByWellName(newVal.wellSearchText),
        perfevalsByCustomerName(newVal.customerSearchText),
        perfevalsByDateRange(newVal.dateRange)
      ]
    });

    isEmpty.value =
      !newVal.globalSearchText &&
      !newVal.ownerSearchText &&
      !newVal.customerSearchText &&
      !newVal.wellSearchText &&
      (!newVal.dateRange || newVal.dateRange.length === 0);
    emit("is-empty", isEmpty.value);
  },
  { debounce: 500 }
);

const showAdvanced = ref(props.isAdvanced);
const isAdvanced = ref(props.isAdvanced ?? false);
</script>
